// The look's live state: what this device has learnt (the preview, the battle's look, the global setting) and the hooks
// pages use. One subscription keeps <html data-look> in step, so the look changes before React paints the next frame.
import { useEffect, useLayoutEffect, useRef } from 'react';
import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import type { RoundsSettings } from '@shared/types';
import { api } from '@/lib/api';
import { useGame } from '@/store/game';
import {
  PREVIEW_KEY, applyLook, codeFromLocation, deviceStore, isLook, lookParam, readBattleLooks, readPreview, readSettingsLook,
  rememberBattleLook, rememberSettingsLook, resolveLook, writePreview,
  type BattleLook, type Look, type ResolvedLook, type SettingsLook,
} from './look';

/** Anything that carries a battle's look: a StateSnapshot or PublicBattleInfo (both have code, look and lookLocked). */
export type BattleLookSource = BattleLook & { code?: string };
interface Claim { id: number; info?: BattleLookSource }

export interface LookState {
  preview?: Look;
  settings?: SettingsLook;
  /** Pages that show a battle (useBattleLook), oldest first; the newest with a known look wins. */
  claims: Claim[];
  /** The player's game (the game store's snapshot). */
  game?: BattleLookSource;
  /** Looks this device has seen, by battle code (also kept in localStorage). */
  cache: Record<string, BattleLook>;
  /** Staff switches on the page: while one is there the floating preview switch stays away. */
  staff: number;
  /** Bumped whenever the address changes (back, forward or the router), so the battle in it is read again. */
  route: number;
}
type Slice = Pick<LookState, 'preview' | 'settings' | 'claims' | 'game' | 'cache' | 'route'>;

export const useLookStore = create<LookState>(() => ({ claims: [], cache: {}, staff: 0, route: 0 }));

/** The battle this page shows: a page's own claim, else the game when it is this page's battle, else what this device remembers. */
export function pickBattle(s: Pick<LookState, 'claims' | 'game' | 'cache'>, urlCode?: string): BattleLook | undefined {
  for (let i = s.claims.length - 1; i >= 0; i--) { const info = s.claims[i].info; if (info) return info; }
  if (s.game && (!urlCode || !s.game.code || s.game.code.toUpperCase() === urlCode)) return s.game;
  return urlCode ? s.cache[urlCode] : undefined;
}

const here = () => (typeof window !== 'undefined' ? window.location : { pathname: '/', search: '' });
/** The look for this page now, from the store and the address. */
export function lookFrom(s: Slice, loc: { pathname: string; search: string } = here()): ResolvedLook {
  return resolveLook({ battle: pickBattle(s, codeFromLocation(loc.pathname, loc.search)), settings: s.settings, preview: s.preview });
}
export const lookNow = (): ResolvedLook => lookFrom(useLookStore.getState());

const sameBattle = (a?: BattleLookSource, b?: BattleLookSource) =>
  a === b || (!!a && !!b && a.look === b.look && Boolean(a.lookLocked) === Boolean(b.lookLocked) && (a.code ?? '') === (b.code ?? ''));
const tidy = (info: BattleLookSource): BattleLookSource => ({
  ...(isLook(info.look) ? { look: info.look } : {}), ...(info.lookLocked === true ? { lookLocked: true } : {}), ...(info.code ? { code: info.code.toUpperCase() } : {}),
});

/** Remembers a battle's look (state and device) once its code is known. */
function remember(info: BattleLookSource) {
  if (!info.code) return;
  const code = info.code.toUpperCase();
  const entry: BattleLook = { look: info.look, lookLocked: info.lookLocked };
  rememberBattleLook(deviceStore(), code, entry);
  const cur = useLookStore.getState().cache[code];
  if (!cur || cur.look !== entry.look || Boolean(cur.lookLocked) !== Boolean(entry.lookLocked)) {
    useLookStore.setState((s) => ({ cache: { ...s.cache, [code]: entry } }));
  }
}

/** The game's snapshot changed (phones): its look is the battle's look. */
export function noteGame(snap: BattleLookSource | undefined | null) {
  const next = snap ? tidy(snap) : undefined;
  if (sameBattle(useLookStore.getState().game, next)) return;
  useLookStore.setState({ game: next });
  if (next) remember(next);
}

/** The global setting from GET /studio/settings (staff pages). */
export function noteSettings(s: Pick<RoundsSettings, 'look' | 'lookFrozen'> | SettingsLook | undefined | null) {
  if (!s) return;
  const next: SettingsLook = { look: isLook(s.look) ? s.look : undefined, lookFrozen: s.lookFrozen === true };
  const cur = useLookStore.getState().settings;
  if (cur && cur.look === next.look && cur.lookFrozen === next.lookFrozen) return;
  useLookStore.setState({ settings: next });
  rememberSettingsLook(deviceStore(), next);
}

/** Sets (or, with undefined, ends) this device's preview. Drops ?look= from the address so a reload keeps the choice. */
export function setPreview(look: Look | undefined) {
  const next = isLook(look) ? look : undefined;
  writePreview(deviceStore(), next);
  if (useLookStore.getState().preview !== next) useLookStore.setState({ preview: next });
  try {
    const u = new URL(window.location.href);
    if (u.searchParams.has('look')) { u.searchParams.delete('look'); window.history.replaceState(window.history.state, '', u.toString()); }
  } catch { /* the address stays as it is */ }
}

/** How often the address is compared for in-app navigation (a string comparison, nothing more). */
export const ROUTE_CHECK_MS = 400;
let seq = 0;
let started = false;
/** What startLook set up, so tests can take it down again. */
let teardown: Array<() => void> = [];
/**
 * Call once, before the first render (main.tsx): reads ?look= and what the device remembers, follows the game, and keeps
 * <html data-look> in step from then on. Safe to call again (it only starts once).
 */
export function startLook() {
  if (started || typeof window === 'undefined') return;
  started = true;
  const store = deviceStore();
  const param = lookParam(window.location.search);
  if (param.kind === 'set') writePreview(store, param.look);
  else if (param.kind === 'clear') writePreview(store, undefined);
  useLookStore.setState({ preview: readPreview(store), cache: readBattleLooks(store), settings: readSettingsLook(store) });
  noteGame(useGame.getState().snapshot);
  teardown.push(useGame.subscribe((g, prev) => { if (g.snapshot !== prev.snapshot) noteGame(g.snapshot); }));
  applyLook(lookNow().look);
  teardown.push(useLookStore.subscribe(() => applyLook(lookNow().look)));
  const onPop = () => useLookStore.setState((s) => ({ route: s.route + 1 }));
  // Another tab changed the preview (the builder in one tab, the game in another).
  const onStorage = (e: StorageEvent) => { if (e.key === PREVIEW_KEY) useLookStore.setState({ preview: readPreview(store) }); };
  window.addEventListener('popstate', onPop);
  window.addEventListener('storage', onStorage);
  // The router moves between pages with pushState, which fires no event: notice a new address within a moment.
  let href = window.location.pathname + window.location.search;
  const watch = window.setInterval(() => {
    const now = window.location.pathname + window.location.search;
    if (now !== href) { href = now; onPop(); }
  }, ROUTE_CHECK_MS);
  teardown.push(() => { window.removeEventListener('popstate', onPop); window.removeEventListener('storage', onStorage); window.clearInterval(watch); });
}
/** Tests only: undo startLook and forget everything. */
export function resetLookForTests() {
  teardown.forEach((off) => off()); teardown = [];
  started = false; seq = 0;
  useLookStore.setState({ preview: undefined, settings: undefined, claims: [], game: undefined, cache: {}, staff: 0, route: 0 }, false);
}

// ---------------------------------------------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------------------------------------------
export interface UseLook extends ResolvedLook {
  /** This device's preview override, if any (it may equal the base look). */
  preview?: Look;
  /** Starts a preview of that look on this device, or ends it with undefined. */
  setPreview: (look: Look | undefined) => void;
}
/** The look in force on this page, and the preview control. */
export function useLook(): UseLook {
  const s = useLookStore(useShallow((st): Slice => ({ preview: st.preview, settings: st.settings, claims: st.claims, game: st.game, cache: st.cache, route: st.route })));
  return { ...lookFrom(s), preview: s.preview, setPreview };
}

const useIso = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

/**
 * Pages that show a battle report it: `useBattleLook(snapshot)` or `useBattleLook(publicInfo)`; pass undefined while it
 * loads. The look follows the battle (and its lock) from then on; the claim ends when the page unmounts.
 */
export function useBattleLook(info: BattleLookSource | null | undefined) {
  const id = useRef(0);
  useIso(() => {
    const cid = ++seq; id.current = cid;
    useLookStore.setState((s) => ({ claims: [...s.claims, { id: cid }] }));
    return () => useLookStore.setState((s) => ({ claims: s.claims.filter((c) => c.id !== cid) }));
  }, []);
  const known = info !== undefined && info !== null;
  const look = info?.look; const lookLocked = info?.lookLocked; const code = info?.code;
  useIso(() => {
    const cid = id.current;
    const next = known ? tidy({ look, lookLocked, code }) : undefined;
    useLookStore.setState((s) => ({ claims: s.claims.map((c) => (c.id === cid && !sameBattle(c.info, next) ? { id: cid, info: next } : c)) }));
    if (next) remember(next);
  }, [known, look, lookLocked, code]);
}

/** Staff pages without a battle report the global setting they loaded from GET /studio/settings (undefined while loading). */
export function useSettingsLook(settings: Pick<RoundsSettings, 'look' | 'lookFrozen'> | null | undefined) {
  const look = settings?.look; const frozen = settings?.lookFrozen; const known = Boolean(settings);
  useIso(() => { if (known) noteSettings({ look, lookFrozen: frozen }); }, [known, look, frozen]);
}

/** Reads the global setting with the ops key once per page (a 404, 403 or a network fault just leaves the default). */
export function useSettingsLookFromKey(opsKey: string | undefined) {
  useEffect(() => {
    if (!opsKey) return;
    let live = true;
    api<Partial<RoundsSettings>>('/studio/settings', { headers: { 'X-Ops-Key': opsKey } })
      .then((s) => { if (live && s && typeof s === 'object') noteSettings({ look: s.look, lookFrozen: s.lookFrozen }); })
      .catch(() => { /* the setting is optional: the look stays as it is */ });
    return () => { live = false; };
  }, [opsKey]);
}

/** A staff switch is on the page (the floating preview switch stays away while it is). */
export function useStaffSwitch(on: boolean) {
  useIso(() => {
    if (!on) return;
    useLookStore.setState((s) => ({ staff: s.staff + 1 }));
    return () => useLookStore.setState((s) => ({ staff: Math.max(0, s.staff - 1) }));
  }, [on]);
}
